import Control from "ol/control/Control";
import { distanceAlongCm, row, rowHeader, rowHint, toLatLon } from "../util";
import {
  curvatureAtJunctionKeyValues,
  maxSpeedDisplayFromCurvature,
  orbisCurvatureToNds,
} from "../layers/style/curvature";
import { lineString, point } from "@turf/helpers";
import { pointToLineDistance } from "@turf/turf";
import nearestPointOnLine from "@turf/nearest-point-on-line";
import { LineString } from "ol/geom";
import { LRChainEncoded, ValueAtProducer } from "@orbis-lr-sdk/orbis-lr-sdk";
import { buildTangentFeature } from "../layers/HoverLayer";
import { splitLaneList } from "../layers/style/util/laneLists.mjs";
import {
  BASEMAP_LAYER_ID,
  compareLaneFunction,
  getLaneFunctionOf,
  LANE_FUNCTION_LAYER_ID,
} from "../layers/style/util/laneFunction.mjs";
import {
  isActiveAt,
  parseTimeDomain,
  splitConditionalValue,
} from "../layers/style/util/conditionalValues.mjs";
import { moveLiveCursor } from "../featureinfo/entryGlue.js";
import { buildFeatureModel } from "../featureinfo/featureModel.mjs";
import { fcdHeadingDeg, hoverView } from "../featureinfo/hoverRows.mjs";
import { valueText } from "../featureinfo/renderValues.mjs";
import {
  MAX_SPEED_LABEL,
  NDS_CURVATURE_LABEL,
  RADIUS_LABEL,
} from "../layers/style/util/curvatureSpeed.mjs";
import { NO_PREFIX_LABEL } from "../featureinfo/featureModel.mjs";
import { staleHoverLayers } from "../featureinfo/hoverClear.mjs";

// Skips the redraw an empty source's clear() would still trigger on every move.
const clearSource = (source) => {
  if (!source.isEmpty()) source.clear();
};

// The hover box rebuilds on every pointermove, the model only when the feature did:
// feature.set() does not bump the revision, so handlers adding properties (lengthCm,
// relation-id, …) are caught by the key count.
const hoverModels = new WeakMap();
function hoverModelOf(feature) {
  const cached = hoverModels.get(feature);
  const keyCount = feature.getKeys().length;
  if (
    cached &&
    cached.revision === feature.getRevision() &&
    cached.keyCount === keyCount
  )
    return cached.model;
  const length = feature.get("lengthCm");
  const model = buildFeatureModel({
    id: String(feature.getId() ?? ""),
    properties: feature.getProperties(),
    geometryType: feature.getGeometry()?.getType(),
    lengthCm: Number.isFinite(length) ? length : null,
  });
  hoverModels.set(feature, {
    revision: feature.getRevision(),
    keyCount,
    model,
  });
  return model;
}

export class FeatureInfoControl extends Control {
  constructor() {
    const div = document.createElement("div");
    super({
      element: div,
    });
    const this_ = this;
    this.handlersForLayers = [];
    this.hoverLayersForLayers = [];
    div.id = "feature-info";
    div.className = "ol-unselectable";
    this.attributesTable = document.createElement("table");
    this.attributesTable.className = "ol-selectable feature-info-list";
    div.append(this.attributesTable);

    // listen to changes
    this.on("propertychange", () => {
      // roles
      const selectedFeaturesAndLayers = this_.get("selectedFeaturesAndLayers");
      // Only what is under the pointer stays drawn; the loop below redraws it.
      this_.clearHoverDrawings(selectedFeaturesAndLayers ?? []);
      if (selectedFeaturesAndLayers === undefined) {
        this.element.style.display = "none";
        moveLiveCursor(null);
        return;
      }
      const selectedFeatures = selectedFeaturesAndLayers.map(
        (featureAndLayer) => featureAndLayer.feature,
      );
      const position = this_.get("position");
      const fcdSpeeds = this_.get("fcdSpeeds");
      this.showValuesAtPosition(position, selectedFeatures, fcdSpeeds);
      selectedFeaturesAndLayers.forEach((featureAndLayer) => {
        const feature = featureAndLayer.feature;
        const layer = featureAndLayer.layer;
        this_
          .getHandlersForLayer(layer)
          .forEach((handler) => handler(feature, layer, position));
        const hoverLayers = this_.getHoverLayersForLayer(layer);
        if (hoverLayers.length === 0) return;
        // Only a line gets a tangent: a polygon under the pointer keeps the line's.
        const built = buildTangentFeature(feature, position);
        if (!built) return;
        const keyValuesAt = this.getKeyValuesAt(
          feature,
          distanceAlongCm(built.latLonGeometry, built.closestPointLatLon),
        );
        keyValuesAt.forEach(([k, v]) => built.tangent.set(k, v));
        hoverLayers.forEach((hoverLayer) => {
          clearSource(hoverLayer.getSource());
          hoverLayer.getSource().addFeature(built.tangent);
        });
      });
    });
  }

  getHandlersForLayer(layer) {
    return this.handlersForLayers
      .filter((layerAndHandler) => layerAndHandler.layer === layer)
      .map((layerAndHandler) => layerAndHandler.handler);
  }
  getHoverLayersForLayer(layer) {
    return this.hoverLayersForLayers
      .filter((layerAndHoverLayer) => layerAndHoverLayer.layer === layer)
      .map((layerAndHoverLayer) => layerAndHoverLayer.hoverLayer);
  }
  getAllHoverLayers() {
    return this.hoverLayersForLayers.map(
      (layerAndHoverLayer) => layerAndHoverLayer.hoverLayer,
    );
  }
  /** Tangent layers of layers with no hovered line, and every handler's drawing. */
  clearHoverDrawings(hovered) {
    const lines = hovered.map(({ feature, layer }) => ({
      layer,
      line: feature.getGeometry()?.getType() === "LineString",
    }));
    staleHoverLayers(this.hoverLayersForLayers, lines).forEach((layer) =>
      clearSource(layer.getSource()),
    );
    this.handlersForLayers.forEach(({ handler }) => handler.clear?.());
  }
  /** The pointer left the map (onto a control): drop every hover drawing and the box. */
  clearHover() {
    this.clearHoverDrawings([]);
    this.unset("selectedFeaturesAndLayers");
  }
  addHandlerForLayer(handler, layer) {
    this.handlersForLayers.push({ layer: layer, handler: handler });
  }
  addHoverLayerForLayer(hoverLayer, layer) {
    this.hoverLayersForLayers.push({ layer: layer, hoverLayer: hoverLayer });
  }

  showValuesAtPosition(position, selectedFeatures, fcdSpeeds) {
    const hasFcd = fcdSpeeds && Object.keys(fcdSpeeds).length > 0;
    const closestFeature =
      position !== undefined && selectedFeatures !== undefined
        ? this.findClosestLineFeature(selectedFeatures, position)
        : null;
    if (!closestFeature) {
      moveLiveCursor(null);
      if (!hasFcd) {
        this.element.style.display = "none";
        return;
      }
      this.refreshAttributes(null, null, [], fcdSpeeds);
      return;
    }
    const latLonGeometry = toLatLon(
      lineString(closestFeature.getGeometry().getCoordinates()).geometry,
    );
    const closestPointLatLon = nearestPointOnLine(
      latLonGeometry,
      toLatLon(point(position).geometry),
    );
    const distanceAtFeatureCm = distanceAlongCm(
      latLonGeometry,
      closestPointLatLon,
    );
    moveLiveCursor(closestFeature, distanceAtFeatureCm);
    const keyValuesAt = this.getKeyValuesAt(
      closestFeature,
      distanceAtFeatureCm,
    );
    this.refreshAttributes(
      closestFeature,
      distanceAtFeatureCm,
      keyValuesAt,
      fcdSpeeds,
    );
  }

  findClosestLineFeature(selectedFeatures, position) {
    const latLonEventPoint = toLatLon(point(position).geometry);
    return Array.from(selectedFeatures)
      .filter((feature) => feature.getGeometry() instanceof LineString)
      .sort(
        (f1, f2) =>
          pointToLineDistance(
            latLonEventPoint,
            lineString(f1.getGeometry().getCoordinates()).geometry,
          ) -
          pointToLineDistance(
            latLonEventPoint,
            lineString(f2.getGeometry().getCoordinates()).geometry,
          ),
      )[0];
  }

  refreshAttributes(feature, distanceAtFeatureCm, keyValuesAt, fcdSpeeds) {
    this.attributesTable.innerHTML = "";
    const colGroup = document.createElement("colgroup");
    const colLabel = document.createElement("col");
    colLabel.classList.add("label");
    const colValue = document.createElement("col");
    colValue.classList.add("value");
    colGroup.appendChild(colLabel);
    colGroup.appendChild(colValue);
    this.attributesTable.appendChild(colGroup);
    this.element.style.display = "block";
    this.attributesTable.style = "display: table;";
    if (feature) {
      const view = hoverView(
        hoverModelOf(feature),
        distanceAtFeatureCm,
        keyValuesAt,
      );
      const idRow = rowHeader(view.id);
      idRow.firstChild.className = "fe-hid";
      this.attributesTable.append(idRow);
      this.attributesTable.append(rowHeader(view.positionText));
      view.groups.forEach((group) => {
        const groupRow = rowHeader(group.prefix || NO_PREFIX_LABEL);
        groupRow.firstChild.className = "fe-hg";
        this.attributesTable.append(groupRow);
        group.rows.forEach(({ key, value, rangeText }) => {
          const cell = document.createElement("span");
          cell.textContent = valueText(value);
          if (rangeText) {
            const range = document.createElement("span");
            range.className = "fe-hs";
            range.textContent = rangeText;
            cell.append(" ", range);
          }
          this.attributesTable.append(row(key, cell));
        });
      });
      view.derived.forEach(([key, value]) =>
        this.attributesTable.append(row(key, valueText(value))),
      );
      this.attributesTable.append(rowHint("click feature to see all tags"));
    }
    if (fcdSpeeds && Object.keys(fcdSpeeds).length > 0) {
      this.attributesTable.append(rowHeader("FCD speed at cursor"));
      Object.keys(fcdSpeeds)
        .map((id) => Number(id))
        .sort((a, b) => a - b)
        .forEach((id) => {
          const arrow = document.createElement("span");
          arrow.textContent = "↑";
          arrow.style.display = "inline-block";
          arrow.style.transform = `rotate(${fcdHeadingDeg(id)}deg)`;
          this.attributesTable.append(row(arrow, fcdSpeeds[id] + " km/h"));
        });
    }
  }

  getKeyValuesAt(feature, distanceAtFeatureCm) {
    const keyValuesAt = feature
      .getKeys()
      .filter((key) => key !== "geometry")
      .filter((key) => key !== "type")
      .filter((key) => key !== "lengthCm")
      .filter((key) => key !== "meta-uuid")
      .filter((key) => !key.startsWith("revision-"))
      .filter((key) => {
        const value = feature.get(key);
        return new LRChainEncoded(key, value?.toString() ?? "").isLrEncoded();
      })
      .map((key) => {
        const value = feature.get(key);
        const valueAt = ValueAtProducer.getValueAt(
          key,
          value?.toString(),
          distanceAtFeatureCm,
        );
        return [key, valueAt];
      });
    // Append derived radius and max speed for LR-encoded curvature
    const curvatureEntry = keyValuesAt.find(
      ([key]) =>
        key.endsWith("-curvature:linear") || key.endsWith("-curvature"),
    );
    if (curvatureEntry) {
      const curvature = Number(curvatureEntry[1]);
      if (curvature !== 0 && !isNaN(curvature)) {
        const radius = 10000 / Math.abs(curvature);
        keyValuesAt.push([NDS_CURVATURE_LABEL, orbisCurvatureToNds(curvature)]);
        keyValuesAt.push([RADIUS_LABEL, Math.round(radius) + " m"]);
        keyValuesAt.push([
          MAX_SPEED_LABEL,
          maxSpeedDisplayFromCurvature(curvature),
        ]);
      }
    }

    // Append curvature_at_junction (plain numeric, not LR-encoded) + derived values
    keyValuesAt.push(
      ...curvatureAtJunctionKeyValues(feature.get("curvature_at_junction")),
    );

    // Append the LM lane's road class, derived from its road area by
    // laneRoadClassHandler (a plain value, so the LR filter above drops it).
    const laneRoadClass = feature.get("laneRoadClass");
    if (laneRoadClass !== undefined) {
      keyValuesAt.push(["road class (road area)", laneRoadClass]);
    }

    // The basemap and lane-function layers each tag lane_function; say whether they
    // agree, as the LM Lane Function view does on the map.
    const basemapLaneFunction = getLaneFunctionOf(feature, BASEMAP_LAYER_ID);
    const layerLaneFunction = getLaneFunctionOf(
      feature,
      LANE_FUNCTION_LAYER_ID,
    );
    if (basemapLaneFunction !== undefined || layerLaneFunction !== undefined) {
      const { status } = compareLaneFunction(
        basemapLaneFunction,
        layerLaneFunction,
      );
      keyValuesAt.push([
        "lane_function check",
        `${BASEMAP_LAYER_ID}: ${basemapLaneFunction ?? "–"}, ${LANE_FUNCTION_LAYER_ID}: ${layerLaneFunction ?? "–"} (${status})`,
      ]);
    }

    // Append readable per-lane rows for #Conditional HOV. Read from the FEATURE, not
    // from keyValuesAt: whether the raw row survived the isLrEncoded filter is the
    // SDK's call, but the derived summary must not depend on it.
    keyValuesAt.push(...hovConditionalKeyValues(feature, distanceAtFeatureCm));

    return keyValuesAt;
  }
}

// The position-resolved value of the first feature key containing `part` (the
// step-sectioned variant carries a `:step` suffix, so a contains-test finds both).
function conditionalValueAt(feature, part, distanceAtFeatureCm) {
  const key = feature
    .getKeys()
    .filter((key) => !key.startsWith("revision-"))
    .find((key) => key.includes(part));
  if (!key) return undefined;
  return ValueAtProducer.getValueAt(
    key,
    feature.get(key)?.toString(),
    distanceAtFeatureCm,
  );
}

// One row per HOV lane: its time window, the minimum occupancy, and — when the
// window parses — whether it is active at the browser's clock. The window is written
// in the ROAD's local time, so the row names the timezone it was evaluated in
// instead of pretending the two clocks agree.
function hovConditionalKeyValues(feature, distanceAtFeatureCm) {
  const lanesValue = conditionalValueAt(
    feature,
    "-hov:lanes:conditional",
    distanceAtFeatureCm,
  );
  if (lanesValue === undefined) return [];
  const minimumsValue = conditionalValueAt(
    feature,
    "-hov:minimum:lanes:conditional",
    distanceAtFeatureCm,
  );
  const lanes = splitLaneList(lanesValue);
  const minimums = splitLaneList(minimumsValue);
  const now = new Date();
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const rows = [];
  lanes.forEach((slot, index) => {
    const parsed = splitConditionalValue(slot);
    if (!parsed) return;
    const minimum = splitConditionalValue(minimums[index] ?? "");
    let text = parsed.condition ?? "always";
    if (minimum?.value) {
      text += ` · min occupancy ${minimum.value}`;
    }
    const active = isActiveAt(parseTimeDomain(parsed.condition), now);
    if (active !== null) {
      text += ` · active now (${timeZone}): ${active ? "yes" : "no"}`;
    }
    rows.push([`HOV window, lane ${index + 1} of ${lanes.length}`, text]);
  });
  return rows;
}
